import { ENotificationTemplateType, INotificationTemplateDto, RightPanelType } from "@kuika/kuika-cl-model";
import { notifyDesignerLiveToolIdle } from "../../../services/designer-live/designer-live";
import { DesignerPresenceReporter } from "../../../services/designer-live/designer-presence";
import { NotificationTemplateService } from "../../../services/notification-template-service";
import { PartialContextProvider } from "../../../shared/patial/partial-context-provider";
import { IScreenDesignContextState } from "../screen-design-context";
import { IScreenDesignContextProviderProps, ScreenDesignContextProvider } from "../screen-design-context-provider";

export interface NotificationTemplateProviderState {
  isNotificationTemplatesLoading: boolean;
  notificationTemplates: INotificationTemplateDto[];
  getNotificationTemplates: () => void;
  setEditingNotificationTemplate: (
    template: INotificationTemplateDto | undefined,
    isCreating: boolean,
    notificationTemplateType: ENotificationTemplateType | undefined
  ) => void;
  creatingNotificationTemplate: boolean | undefined;
  editingNotificationTemplate: INotificationTemplateDto | undefined;
  notificationTemplateType: ENotificationTemplateType | undefined;
  hasNotificationTemplateUnsavedChanges: boolean;
  clearNotificationTemplatePanel: () => void;
  setCreateNotificationTemplateActive: () => void;
  isCreateNotificationTemplateActive: boolean;
}

export class NotificationTemplateProvider extends PartialContextProvider<
  ScreenDesignContextProvider,
  IScreenDesignContextProviderProps,
  IScreenDesignContextState,
  NotificationTemplateProviderState
> {
  static defaultState: NotificationTemplateProviderState = {
    isNotificationTemplatesLoading: false,
    notificationTemplates: [],
    getNotificationTemplates: () => {},
    setEditingNotificationTemplate: () => {},
    creatingNotificationTemplate: undefined,
    editingNotificationTemplate: undefined,
    notificationTemplateType: undefined,
    hasNotificationTemplateUnsavedChanges: false,
    clearNotificationTemplatePanel: () => {},
    setCreateNotificationTemplateActive: () => {},
    isCreateNotificationTemplateActive: false
  };

  getProviderValues = (): NotificationTemplateProviderState => {
    return {
      isNotificationTemplatesLoading: this.state.isNotificationTemplatesLoading,
      notificationTemplates: this.state.notificationTemplates,
      getNotificationTemplates: this.getNotificationTemplates,
      setEditingNotificationTemplate: this.setEditingNotificationTemplate,
      creatingNotificationTemplate: this.state.creatingNotificationTemplate,
      editingNotificationTemplate: this.state.editingNotificationTemplate,
      notificationTemplateType: this.state.notificationTemplateType,
      hasNotificationTemplateUnsavedChanges: this.state.hasNotificationTemplateUnsavedChanges,
      clearNotificationTemplatePanel: this.clearNotificationTemplatePanel,
      setCreateNotificationTemplateActive: this.setCreateNotificationTemplateActive,
      isCreateNotificationTemplateActive: this.state.isCreateNotificationTemplateActive
    };
  };

  setEditingNotificationTemplate = (
    template: INotificationTemplateDto | undefined,
    isCreating: boolean,
    notificationTemplateType: ENotificationTemplateType | undefined
  ) => {
    this.partial.designSituationProvider.setActiveRightPanelByClick(RightPanelType.Properties);
    if (isCreating) {
      this.setState({
        creatingNotificationTemplate: true,
        editingNotificationTemplate: undefined,
        notificationTemplateType
      });
      DesignerPresenceReporter.reportEntity("notificationTemplate", undefined, undefined);
    } else {
      this.setState({
        creatingNotificationTemplate: undefined,
        editingNotificationTemplate: template,
        notificationTemplateType
      });
      DesignerPresenceReporter.reportEntity(
        "notificationTemplate",
        template?.id,
        template?.name,
        "notificationTemplate.changed"
      );
    }
  };

  clearNotificationTemplatePanel = () => {
    DesignerPresenceReporter.reportEntity("notificationTemplate", undefined, undefined);
    this.setState(
      {
        creatingNotificationTemplate: undefined,
        editingNotificationTemplate: undefined,
        notificationTemplateType: undefined,
        isCreateNotificationTemplateActive: false
      },
      () => notifyDesignerLiveToolIdle()
    );
  };

  getNotificationTemplates = async () => {
    const response = await NotificationTemplateService.GetNotificationTemplates(this.state.project.id);
    if (response.status === 200) {
      this.setState({ notificationTemplates: response.data as INotificationTemplateDto });
    }
  };

  setCreateNotificationTemplateActive = () => {
    this.setState({ isCreateNotificationTemplateActive: true });
  };
}
